import { ExpandLess } from '@emotion-icons/material/ExpandLess'
import { ExpandMore } from '@emotion-icons/material/ExpandMore'
import styled from '@emotion/styled'
import { IconButton, Pagination, Typography } from '@mui/material'
import React, { FC, memo, useState } from 'react'
import { Point } from 'src/common-services'
import { Dropdown, DropdownOption } from 'src/components/dropdown'
import { FlexColumn, FlexRow } from 'src/components/flex-containers'
import { Colors, Shapes } from 'src/components/theme'
import { ShadowScroll } from 'src/v2/components/shadow-scroll'
import { InsightEmpty } from 'src/views/insight-empty'

const Wrapper = styled.div`
  :last-of-type {
    padding-bottom: ${Shapes.spacing[0]}px;
  }
`
const Filters = styled.div`
  position: relative;
  padding: ${Shapes.spacing[2]}px ${Shapes.spacing[4]}px;
`

const Header = styled.div`
  padding: ${Shapes.spacing[4]}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${Colors.bgWhite};
`

const Content = styled.div`
  padding: ${Shapes.spacing[0]}px ${Shapes.spacing[4]}px ${Shapes.spacing[4]}px ${Shapes.spacing[4]}px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ItemContainer = styled.div<{ disabled?: 1 | 0 }>`
  display: flex;
  flex: 1;
  align-items: center;
  padding: ${Shapes.spacing[2]}px;
  border-style: solid;
  border-width: 1px;
  border-color: ${(props) => (props.disabled ? 'transparent' : Colors.border)};
  border-radius: ${Shapes.borderRadius}px;
  gap: ${Shapes.spacing[2]}px;
  background-color: ${(props) => (props.disabled ? Colors.neutral50 : Colors.bgWhite)};

  :hover {
    background-color: ${(props) => (props.disabled ? Colors.neutral50 : Colors.neutral50)};
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  }
`

const IconWrapper = styled.div<{ bgColor: string }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.bgColor};
  color: white;
`

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[2]}px;
  overflow: hidden;
  flex: 1;
`

const SDropdown = styled(Dropdown)`
  & input {
    color: ${Colors.neutral500} !important;
  }
`

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
`

export type SidebarResultListItem = {
  id: string
  color: string
  icon: JSX.Element
  // In order top to bottom
  header?: JSX.Element
  title: string
  subtitle?: React.ReactNode
  footer?: JSX.Element
  type?: string
  point?: Point
  additional?: React.ReactNode
}

type ResultItemProps = {
  item: SidebarResultListItem
  onClick?: () => void
  selected?: boolean
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  mini?: boolean
  maxWidth?: number
  disabled?: boolean
  additional?: React.ReactNode
}
export const ResultItem: FC<ResultItemProps> = memo(
  ({ item, onClick, onMouseEnter, onMouseLeave, selected, mini, maxWidth, disabled, additional }) => {
    return (
      <div>
        <ItemContainer
          disabled={disabled ? 1 : 0}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          style={{ borderColor: disabled ? 'transparent' : selected ? item.color : Colors.neutral100 }}>
          <IconWrapper bgColor={item.color}>{item.icon}</IconWrapper>
          <ItemContent
            style={{
              gap: mini ? Shapes.spacing[1] : Shapes.spacing[2],
              maxWidth: maxWidth ? maxWidth : mini ? 150 : undefined,
              minWidth: mini ? 150 : maxWidth,
            }}>
            <FlexColumn gap={mini ? Shapes.spacing[1] / 2 : Shapes.spacing[1]}>
              {item.header}
              <Typography variant={mini ? 'text1' : 'textmd'} color={Colors.neutral950} noWrap>
                {item.title}
              </Typography>
              {item.subtitle && (
                <Typography variant={mini ? 'text2m' : 'text1m'} color={Colors.neutral500} noWrap>
                  {item.subtitle}
                </Typography>
              )}
            </FlexColumn>
            {item.footer}
          </ItemContent>
        </ItemContainer>
        {additional}
      </div>
    )
  }
)

type SidebarResultListProps = {
  id?: string
  title: React.ReactNode
  filters?: React.ReactNode
  sortOptions?: DropdownOption[]
  sortOptionId?: string
  onChangeSortOption?: (value: string) => void
  items: SidebarResultListItem[]
  selectedItems?: SidebarResultListItem[]
  onClickItem?: (item: SidebarResultListItem) => void
  onMouseEnterItem?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, item: SidebarResultListItem) => void
  onMouseLeaveItem?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, item: SidebarResultListItem) => void
  page: number
  setPage: (page: number) => void
  totalPages: number
  collapsible?: boolean
  bgColor?: string
  hideEmpty?: boolean
  customEmptyMessage?: React.ReactNode
}
export const SidebarResultList: FC<SidebarResultListProps> = ({
  id,
  items,
  title,
  filters,
  sortOptions,
  sortOptionId,
  selectedItems = [],
  onClickItem,
  onChangeSortOption,
  onMouseEnterItem,
  onMouseLeaveItem,
  page,
  setPage,
  totalPages,
  collapsible,
  bgColor,
  hideEmpty = false,
  customEmptyMessage,
}) => {
  const [collapsed, setCollapsed] = useState(collapsible)
  const handleSortOptionChange = (newVal: string | null) => {
    if (newVal) {
      onChangeSortOption?.(newVal)
    }
  }
  const onToggleCollapse = () => setCollapsed(!collapsed)

  if (hideEmpty && items.length === 0) {
    return null
  }

  return (
    <Wrapper>
      {filters && <Filters>{filters}</Filters>}
      <Header
        id={id}
        style={{
          position: collapsible && collapsed ? 'relative' : 'sticky',
          top: 0,
          zIndex: 1000,
          boxShadow: collapsible && !collapsed ? '0px 4px 16px 0px rgba(0, 0, 0, 0.1)' : 'none',
        }}>
        <FlexRow align="center" gap={Shapes.spacing[1]} flex="1">
          {collapsible ? (
            <IconButton size="small" onClick={onToggleCollapse}>
              {collapsed ? <ExpandMore size={16} /> : <ExpandLess size={16} />}
            </IconButton>
          ) : null}
          {typeof title === 'string' ? (
            <Typography variant="textmd" color={Colors.neutral700}>
              {title}
            </Typography>
          ) : (
            title
          )}
        </FlexRow>
        {sortOptions && sortOptionId && (
          <SDropdown value={sortOptionId} options={sortOptions} onChange={handleSortOptionChange} />
        )}
      </Header>
      {collapsible && collapsed ? (
        <ShadowScroll height={`250px`} isNested>
          <Content
            style={
              bgColor
                ? {
                    backgroundColor: bgColor,
                    paddingTop: `${Shapes.spacing[4]}px`,
                  }
                : {}
            }>
            {items.length === 0 && (
              <>
                {customEmptyMessage ? (
                  customEmptyMessage
                ) : (
                  <InsightEmpty
                    title="No results found"
                    message="Try changing your search criteria or zooming out to see more results."
                  />
                )}
              </>
            )}
            {items.map((item) => {
              const selected = selectedItems.some((x) => x.id === item.id)
              return (
                <ResultItem
                  key={item.id}
                  item={item}
                  onClick={() => onClickItem?.(item)}
                  selected={selected}
                  onMouseEnter={(e) => onMouseEnterItem?.(e, item)}
                  onMouseLeave={(e) => onMouseLeaveItem?.(e, item)}
                  additional={item.additional}
                />
              )
            })}
            {totalPages > 1 && (
              <PaginationWrapper>
                <Pagination
                  size="small"
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  showLastButton
                  showFirstButton
                  page={page}
                  count={totalPages}
                  onChange={(_, newPage) => setPage(newPage)}
                  hidden={totalPages <= 1}
                  boundaryCount={0}
                  siblingCount={1}
                />
              </PaginationWrapper>
            )}
          </Content>
        </ShadowScroll>
      ) : (
        <Content
          style={
            bgColor
              ? {
                  backgroundColor: bgColor,
                  paddingTop: `${Shapes.spacing[4]}px`,
                }
              : {}
          }>
          {items.length === 0 && (
            <>
              {customEmptyMessage ? (
                customEmptyMessage
              ) : (
                <InsightEmpty
                  title="No results found"
                  message="Try changing your search criteria or zooming out to see more results."
                />
              )}
            </>
          )}
          {items.map((item) => {
            const selected = selectedItems.some((x) => x.id === item.id)
            return (
              <ResultItem
                key={item.id}
                item={item}
                onClick={() => onClickItem?.(item)}
                selected={selected}
                onMouseEnter={(e) => onMouseEnterItem?.(e, item)}
                onMouseLeave={(e) => onMouseLeaveItem?.(e, item)}
                additional={item.additional}
              />
            )
          })}
          {totalPages > 1 && (
            <PaginationWrapper>
              <Pagination
                size="small"
                variant="outlined"
                shape="rounded"
                color="primary"
                showLastButton
                showFirstButton
                page={page}
                count={totalPages}
                onChange={(_, newPage) => setPage(newPage)}
                hidden={totalPages <= 1}
                boundaryCount={0}
                siblingCount={1}
              />
            </PaginationWrapper>
          )}
        </Content>
      )}
    </Wrapper>
  )
}
